import { Check, Copy } from 'lucide-react'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { copyText } from '../lib/clipboard'
import { Button } from './Button'
import { Dialog } from './Dialog'
import { toast } from './Toast'

// Буфер обмена недоступен (бывает в WebView): текст показывается в листе,
// уже выделенным — остаётся «Скопировать» из меню телефона. Один лист на приложение.

interface ManualState {
  title: string
  text: string
  prose: boolean
}

let current: ManualState | null = null
const listeners = new Set<() => void>()

function set(next: ManualState | null) {
  current = next
  listeners.forEach((cb) => cb())
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function showManualCopy(text: string, title = 'Скопируйте вручную', prose = false): void {
  set({ text, title, prose })
}

/**
 * Скопировать, а если буфер обмена недоступен — «Не вышло скопировать» и лист
 * с текстом. Звать прямо из нажатия, без сетевых ожиданий перед ним.
 */
export async function copyOrShow(
  text: string,
  { done = 'Скопировано', title, prose = false }: { done?: string; title?: string; prose?: boolean } = {},
): Promise<boolean> {
  if (await copyText(text)) {
    toast(done)
    return true
  }
  toast('Не вышло скопировать', { error: true })
  showManualCopy(text, title, prose)
  return false
}

/** Ставится один раз в корне приложения, рядом с Toaster. */
export function ManualCopyHost() {
  const state = useSyncExternalStore(subscribe, () => current)
  if (!state) return null
  return <ManualCopySheet key={state.text} state={state} onClose={() => set(null)} />
}

function ManualCopySheet({ state, onClose }: { state: ManualState; onClose: () => void }) {
  const field = useRef<HTMLTextAreaElement | null>(null)
  const [copied, setCopied] = useState(false)
  const selectAll = () => {
    const el = field.current
    if (!el) return
    el.focus({ preventScroll: true })
    el.select()
    el.setSelectionRange(0, el.value.length)
  }
  useEffect(selectAll, [])

  return (
    <Dialog
      title={state.title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              if (await copyText(state.text)) {
                setCopied(true)
                toast('Скопировано')
              } else {
                selectAll()
              }
            }}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Скопировано' : 'Копировать'}
          </Button>
        </>
      }
    >
      <p className="mb-2 text-sm text-pretty text-muted">
        Буфер обмена недоступен. Текст уже выделен — нажмите на него и выберите «Скопировать».
      </p>
      <textarea
        ref={field}
        readOnly
        value={state.text}
        rows={Math.min(8, Math.max(2, Math.ceil(state.text.length / 40)))}
        onFocus={selectAll}
        aria-label="Текст для копирования"
        className={`w-full resize-none rounded-lg bg-surface px-3 py-2 text-base ring-1 ring-line outline-none ring-inset ${state.prose ? 'font-sans leading-relaxed break-words desk:text-[15px]' : 'font-mono break-all desk:text-xs'}`}
      />
    </Dialog>
  )
}
